import { useEffect, useState } from 'react';
import { getPublicSession, setShareToken } from '../../services/api.js';
import {
  getMe, tryRefresh, clearTokens, logout, stopMasquerade, REFRESH_FAILED,
} from '../../services/auth.js';
import App from '../../App.jsx';

const centerStyle = {
  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
  minHeight: '100vh', background: '#0f1117', color: '#9ca3af', fontSize: '1rem',
  textAlign: 'center', padding: '2rem', gap: '0.75rem',
};

/**
 * Entry point for an account-free public share link (`/s/<token>`).
 *
 * Resolves the token to its session, registers the token so every subsequent
 * read call carries the `X-Share-Token` header, then renders the normal App in
 * read-only `publicMode`. A revoked/invalid token shows a friendly dead-end with
 * a sign-up call to action.
 *
 * If the visitor is already logged in we recognise them: owners of the shared
 * session are routed to the full editable view (`/app?session=<id>`), and other
 * logged-in viewers see the read-only view with their account in context (no
 * "sign up" prompt). The share token — not the bearer — authorises reads in
 * public mode (see authFetch), so the read-only view works regardless of whether
 * the logged-in viewer has any independent access to the session.
 */
export default function PublicGate({ token }) {
  const [state, setState] = useState('loading'); // loading | ready | error
  const [sessionId, setSessionId] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    let cancelled = false;

    (async () => {
      // Recognise an existing login (refresh once if the access token expired).
      let user = await getMe();
      if (!user && (await tryRefresh()) !== REFRESH_FAILED) user = await getMe();
      if (cancelled) return;

      try {
        // Pass the bearer (if any) so the backend can report is_owner.
        const payload = await getPublicSession(token);
        if (cancelled) return;

        // Owner viewing their own link → send them to the full editable view.
        if (user && payload.is_owner) {
          window.location.replace(`/app?session=${encodeURIComponent(payload.session_id)}`);
          return;
        }

        setShareToken(token);
        setCurrentUser(user || null);
        setSessionId(payload.session_id);
        setState('ready');
      } catch (err) {
        if (cancelled) return;
        setErrorMsg(err?.message || 'This shared link is no longer available.');
        setState('error');
      }
    })();

    return () => { cancelled = true; };
  }, [token]);

  if (state === 'loading') {
    return <div style={centerStyle}>Loading shared session…</div>;
  }

  if (state === 'error') {
    return (
      <div style={centerStyle}>
        <div style={{ fontSize: '1.25rem', color: '#e5e7eb', fontWeight: 600 }}>
          Link unavailable
        </div>
        <div style={{ maxWidth: 420 }}>{errorMsg}</div>
        <a
          href="/app"
          onClick={(e) => { e.preventDefault(); window.location.replace('/app'); }}
          style={{
            marginTop: '0.5rem', padding: '0.55rem 1.1rem', borderRadius: 8,
            background: '#2563eb', color: '#fff', textDecoration: 'none', fontWeight: 600,
          }}
        >
          Go to TrackDay Analysis
        </a>
      </div>
    );
  }

  return (
    <App
      publicMode
      initialSessionId={sessionId}
      currentUser={currentUser}
      onStopMasquerade={currentUser?.masquerade ? async () => {
        await stopMasquerade();
        window.location.replace('/app');
      } : undefined}
      onLogout={currentUser ? () => {
        logout();
        clearTokens();
        setCurrentUser(null);
      } : undefined}
    />
  );
}
